import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import backendClient from '../services/api/backendClient';
import {
  IOrganization,
  IOrganizationWithRelations,
} from '@interfaces/organization';
import { toast } from 'react-toastify';
import { CreateOrganizationType } from '@dto/organization/organization.dto';
import { RecrawlSchedule } from '@enums/recrawl-schedule.enum';
import { HttpStatusCode, isAxiosError } from 'axios';
import { useAuth } from '@contexts/AuthContext';

interface OrganizationContextProps {
  /* properties */
  organizations: IOrganization[];
  loading: boolean;

  /* methods */
  saveOrganization: (
    organizationData: CreateOrganizationType
  ) => Promise<IOrganization>;
  getOrganizationById: (
    organizationId: string
  ) => Promise<IOrganizationWithRelations>;
  deleteOrganization: (organizationId: string) => Promise<string>;
  updateOrganizationField: <K extends keyof IOrganization>(
    organizationId: string,
    field: K,
    value: IOrganization[K]
  ) => Promise<void>;
  updateOrganizationRecrawlSchedule: (
    organizationId: string,
    recrawlSchedule: RecrawlSchedule
  ) => Promise<void>;
  runRecrawl: (organizationId: string) => Promise<void>;
  stopCrawl: (organizationId: string) => Promise<void>;
  updateOrganizationAutomatedStatus: (
    organizationId: string,
    isAutomated: boolean
  ) => Promise<void>;
  refetchOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextProps>({
  /* properties */
  organizations: [],
  loading: true,

  /* methods */
  saveOrganization: async () => {
    return {} as IOrganization;
  },
  getOrganizationById: async () => {
    return {} as IOrganizationWithRelations;
  },
  deleteOrganization: async () => '',
  updateOrganizationRecrawlSchedule: async () => {},

  runRecrawl: async () => {},
  stopCrawl: async () => {},
  updateOrganizationAutomatedStatus: async () => {},

  updateOrganizationField: async () => {},
  refetchOrganizations: async () => {},
});

interface OrganizationProviderProps {
  children: ReactNode;
}
export const OrganizationProvider = ({
  children,
}: OrganizationProviderProps) => {
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { isAuthenticated, loading: loadingUser } = useAuth();

  const fetchOrganizations = async () => {
    try {
      const { data } =
        await backendClient.get<IOrganization[]>('/organizations');
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Error fetching organizations');
    } finally {
      setLoading(false);
    }
  };

  const refetchOrganizations = async () => {
    try {
      const { data } =
        await backendClient.get<IOrganization[]>('/organizations');
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Error fetching organizations');
    }
  };

  useEffect(() => {
    if (!loadingUser && isAuthenticated) {
      fetchOrganizations();
    }
  }, [isAuthenticated, loadingUser]);

  const getOrganizationById = async (
    organizationId: string
  ): Promise<IOrganizationWithRelations> => {
    const { data } = await backendClient.get<IOrganizationWithRelations>(
      `/organizations/${organizationId}`
    );
    return data;
  };

  const saveOrganization = async (
    organizationData: CreateOrganizationType
  ): Promise<IOrganization> => {
    const { data } = await backendClient.post<IOrganization>(
      '/organizations',
      organizationData
    );

    if (organizationData.id) {
      updateOrganizationsState(organizationData.id, data);
    } else {
      setOrganizations((prev) => [...prev, data]);
    }
    return data;
  };

  const deleteOrganization = async (
    organizationId: string
  ): Promise<string> => {
    const { data } = await backendClient.delete<{ message?: string }>(
      `/organizations/${organizationId}`
    );
    setOrganizations((previousOrganizations) =>
      previousOrganizations.filter(
        (organization) => organization.id !== organizationId
      )
    );
    return typeof data?.message === 'string' && data.message.trim()
      ? data.message
      : 'Organization deleted';
  };

  const updateOrganizationsState = (
    organizationId: string,
    updatedOrganization: IOrganization
  ): void => {
    setOrganizations((prev) =>
      prev.map((organization) =>
        organization.id === organizationId ? updatedOrganization : organization
      )
    );
  };

  const updateOrganizationRecrawlSchedule = async (
    organizationId: string,
    recrawlSchedule: RecrawlSchedule
  ): Promise<void> => {
    const { data } = await backendClient.put<IOrganization>(
      `/organizations/${organizationId}/recrawl-schedule`,
      { recrawlSchedule }
    );
    await updateOrganizationField(
      organizationId,
      'recrawlSchedule',
      data.recrawlSchedule
    );
  };

  const runRecrawl = async (organizationId: string): Promise<void> => {
    try {
      await backendClient.post(`/organizations/${organizationId}/recrawl`);
      await updateOrganizationField(organizationId, 'isCrawled', true);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.Conflict) {
          await updateOrganizationField(organizationId, 'isCrawled', true);
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }
  };

  const stopCrawl = async (organizationId: string): Promise<void> => {
    await backendClient.post(`/organizations/${organizationId}/stop-crawl`);
    await updateOrganizationField(organizationId, 'isCrawled', false);
  };

  const updateOrganizationAutomatedStatus = async (
    organizationId: string,
    isAutomated: boolean
  ): Promise<void> => {
    const { data } = await backendClient.put<{ isAutomated: boolean }>(
      `/organizations/${organizationId}/automated-status`,
      { isAutomated }
    );
    await updateOrganizationField(
      organizationId,
      'isAutomated',
      data.isAutomated
    );
  };

  const updateOrganizationField = async <K extends keyof IOrganization>(
    organizationId: string,
    field: K,
    value: IOrganization[K]
  ): Promise<void> => {
    const organizationToUpdate = organizations.find(
      (org) => org.id === organizationId
    );
    if (!organizationToUpdate) return;
    organizationToUpdate[field] = value;

    updateOrganizationsState(organizationId, organizationToUpdate);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        loading,
        saveOrganization,
        getOrganizationById,
        deleteOrganization,
        updateOrganizationField,
        updateOrganizationRecrawlSchedule,
        runRecrawl,
        stopCrawl,
        updateOrganizationAutomatedStatus,
        refetchOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizations = () => useContext(OrganizationContext);
